import React, { FC, useState, useMemo } from "react";
import { Card, Button, Tabs, Tooltip, Spin } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { CardProps } from "antd/lib/card";
import classnames from "classnames";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { omit } from "lodash";

import styles from "./styles.module.less";
import { useTranslation } from "react-i18next";

export interface ConfigCardProps extends CardProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  onSave?: React.MouseEventHandler<HTMLElement>;
  loading?: boolean;
}

/**
 * 普通的配置卡片，头部左侧可以显示 title，description，右侧有保存按钮
 */
export const ConfigCard: FC<ConfigCardProps> = ({
  title,
  extra,
  onSave,
  children,
  className,
  description,
  loading = false,
  ...cardProps
}) => {
  const { t } = useTranslation();

  if (extra === undefined) {
    extra = (
      <Button
        style={{
          padding: 0,
        }}
        type="link"
        icon={<SaveOutlined />}
        onClick={onSave}
        size="small"
      >
        {t("common.save")}
      </Button>
    );
  }

  return (
    <Card
      className={classnames(styles.configCard, className, {
        [styles.noHeader]: !extra && !title,
      })}
      title={
        <>
          <h3
            style={{
              fontSize: 16,
              marginBottom: 0,
            }}
          >
            {title}
          </h3>
          {description && (
            <div className={styles.description}>{description}</div>
          )}
        </>
      }
      extra={extra}
      {...cardProps}
    >
      <Spin spinning={loading}>{children}</Spin>
    </Card>
  );
};

/**
 * 头部左侧可以切换的配置卡片
 */
export interface ConfigCardWithTabProps {
  tabs: ({
    title: string;
    tips?: string;
    key: string;
    component: React.ReactNode;
  } & CardProps)[];
  className?: string;
  activeKey?: string;
  defaultActiveKey?: string;
  onTabChange?: (key: string) => void;
}
export const ConfigCardWithTab: FC<ConfigCardWithTabProps> = ({
  tabs,
  activeKey,
  defaultActiveKey,
  onTabChange,
}) => {
  const [localActiveKey, setLocalActiveKey] = useState(
    defaultActiveKey || activeKey || tabs[0].key
  );

  const mergedActiveKey = useMemo(() => {
    return activeKey || localActiveKey;
  }, [activeKey, localActiveKey]);

  const currTabConfig = useMemo(() => {
    return tabs.find((item) => item.key === mergedActiveKey) || tabs[0];
  }, [mergedActiveKey, tabs]);

  return (
    <Card
      className={styles.configCard}
      title={
        <Tabs
          onChange={(key) => {
            if (activeKey) {
              onTabChange?.(key);
            } else {
              setLocalActiveKey(key);
            }
          }}
          activeKey={mergedActiveKey}
          className={styles.cardHeaderTab}
        >
          {tabs.map((tab) => {
            return (
              <Tabs.TabPane
                key={tab.key}
                tabKey={tab.key}
                tab={
                  <span>
                    {tab.title}{" "}
                    {tab.tips && (
                      <Tooltip title={tab.tips}>
                        <QuestionCircleOutlined style={{ color: "#9ba1a7" }} />
                      </Tooltip>
                    )}
                  </span>
                }
              />
            );
          })}
        </Tabs>
      }
      {...omit(currTabConfig, "title", "tips", "key", "component")}
    >
      {currTabConfig.component}
    </Card>
  );
};

export interface ConfigHeaderCardProps extends CardProps {
  queryTabKey?: string;
}
